import type { HistoryOrder } from '../../api/history';
import { uiFormat, uiText } from '../../i18n';

const names: Record<string, string> = {
  tts: 'Озвучка',
  circle: 'Кружок',
  who: 'Кружок',
  video: 'Видео',
  reels: 'Рилс',
  lipsync: 'Говорящее видео',
  dance: 'Повтори движения',
  photo: 'Фото',
  shoot: 'Фотосессия',
  pshoot: 'Фотосессия товара',
  audio: 'Аудио',
  voice_s2s: 'Смена голоса',
  clean: 'Почистить запись',
  transcription: 'Расшифровка',
  text: 'Текст',
  script: 'Озвучка проекта',
  avatar: 'Свой аватар',
  collage: 'Коллаж',
  cover: 'Кавер',
  slides: 'Слайды',
  post: 'Карусель',
};
/** Имя вида работы — русский ключ словаря (движок «Истории» переводит его при отрисовке, чтобы смена языка не оставляла прежний) */
export const orderTitleKey = (order: HistoryOrder) => names[order.screen] || names[order.kind] || 'Работа';
export const orderTitle = (order: HistoryOrder) => order.title || uiText(orderTitleKey(order));
export const isActive = (order: HistoryOrder) =>
  ['queued', 'running', 'processing', 'waiting_pipeline', 'provider_started'].includes(order.status);
export function deliveryFormats(order: HistoryOrder): string[] {
  if (order.result?.available === false) return [];
  const explicit = order.allowed_formats ?? order.delivery?.allowed_formats;
  if (explicit) return explicit;
  // Совместимость с опубликованным TTS-контрактом; для product ждём capability сервера.
  if (order.kind === 'tts') return ['audio', 'document'];
  return [];
}

/** Finance labels describe only confirmed facts, never an expected refund.
    label и note — русские ключи словаря: движок «Истории» переводит их при отрисовке (и сравнивает label с русскими подписями). */
export function financialDisplay(order: HistoryOrder) {
  const state = order.financial?.state;
  const refunded = state === 'refunded' || (!state && Number(order.charge?.refunded) > 0);
  const pending = ['refund_pending', 'refund_unknown'].includes(state ?? '');
  const declined = ['debit_failed', 'debit_declined'].includes(state ?? '');
  const free = ['not_required', 'dev_free'].includes(state ?? '');
  const notCharged = declined || ['debit_pending', 'debit_unknown', 'pending'].includes(state ?? '');
  const raw = (
    refunded
      ? (order.financial?.refunded_amount ?? order.charge?.refunded ?? order.financial?.amount_tokens)
      : (order.financial?.charged_amount ?? order.charge?.debited ?? order.financial?.amount_tokens ?? order.price)
  );
  const amount = free ? 0 : raw == null ? null : Number(raw);
  const charged = state === 'charged' || state === 'debited' || (!state && Number(order.charge?.debited) > 0);
  return {
    free,
    refunded,
    pending,
    amount: amount != null && Number.isFinite(amount) ? Math.abs(amount) : null,
    label: free
      ? 'Цена'
      : refunded
        ? 'Вернули'
        : pending
          ? 'Возврат обрабатывается'
          : notCharged
            ? declined
              ? 'Не списано'
              : 'Списание не подтверждено'
            : charged
              ? 'Списано'
              : 'Списание не подтверждено',
    note: free
      ? 'Circle не списаны'
      : refunded
        ? 'Circle вернулись на баланс'
        : pending
          ? 'Возврат обрабатывается'
          : notCharged
            ? declined
              ? 'Circle не списаны'
              : 'Списание пока не подтверждено'
            : charged && amount != null && Number.isFinite(amount)
              ? `${Math.abs(amount)} C списаны`
              : 'Возврат не подтверждён',
  };
}

export function activeProgress(order: HistoryOrder) {
  const steps = order.steps ?? [];
  const total = steps.length || order.steps_count || 0;
  const index = Math.max(0, order.step_index ?? steps.findIndex(step => (step.state ?? step.status) === 'running'));
  const progress = order.progress ?? steps[index]?.progress;
  const partial =
    typeof progress === 'number'
      ? progress > 1
        ? progress / 100
        : progress
      : progress && progress.total > 0
        ? progress.done / progress.total
        : null;
  const fraction = partial == null ? null : Math.max(0, Math.min(1, partial));
  const step = total ? Math.min(index + 1, total) : null;
  const label =
    order.execution_state === 'waiting_finance'
      ? uiText('Ожидаем подтверждения оплаты')
      : order.execution_state === 'reconciling' || order.status === 'uncertain'
        ? uiText('Проверяем результат')
        : total
          ? uiFormat('Шаг {0} из {1}', step, total)
          : uiText('Создаётся');
  return {
    label,
    fraction: total ? Math.min(1, (index + (fraction ?? 0)) / total) : fraction,
    note: typeof progress === 'object' ? progress?.note : undefined,
  };
}

export const needsOrderSync = (order: HistoryOrder) =>
  isActive(order) ||
  order.status === 'uncertain' ||
  ['debit_pending', 'debit_unknown', 'refund_pending', 'refund_unknown'].includes(order.financial?.state ?? '') ||
  ['pending', 'queued', 'accepted', 'sending', 'uncertain'].includes(order.delivery?.state ?? '');
